// These functions generate transformations for the `page_content` field.
// They handle the common cases for CMS actions.

// Considerations:
// - For this initial implementation, indexing is managed using the array positioning,
//   which may become complicated with rendering and DnD in the future.

import { concat, filter, get, isArray, map, some } from "lodash";

import {
  BuilderIOTabOptions,
  GalleryTabDataImage,
  GalleryTabDataVideo,
  GalleryTabOptions,
  MusicTabData,
  MusicTabDataOptions,
  ProductTabOptions,
  SKUPageContent,
  SKUPageContentGenericTabOptions,
  SKUPageContentTab,
  SKUPageContentTabType,
  SKUPageContentType,
} from "@/data/types";

export const MAX_TABS = 5;

export const CREATABLE_TAB_TYPES: SKUPageContentTabType[] = [
  SKUPageContentTabType.MUSIC_TAB,
  SKUPageContentTabType.GALLERY_TAB,
  SKUPageContentTabType.BUILDER_IO_TAB,
];

const EMPTY_AUDIO_TAB: SKUPageContentTab = {
  type: SKUPageContentTabType.MUSIC_TAB,
  version: "1.0.0",
  data: [],
  options: {
    title: "",
    description: "",
    show_header_title: true,
    show_header_description: true,
    show_audio_artist_name: true,
  },
};

const EMPTY_BUILDER_IO_TAB: SKUPageContentTab = {
  type: SKUPageContentTabType.BUILDER_IO_TAB,
  version: "1.0.0",
  options: {
    builder_io_path: "",
    builder_io_api_key: "",
  },
};

const EMPTY_GALLERY_TAB: SKUPageContentTab = {
  type: SKUPageContentTabType.GALLERY_TAB,
  version: "1.0.0",
  data: [],
  options: {
    title: "",
    description: "",
    show_header_title: true,
    show_header_description: true,
    show_content_titles: true,
    show_content_descriptions: true,
  },
};

const EMPTY_PRODUCT_TAB: SKUPageContentTab = {
  type: SKUPageContentTabType.PRODUCT_TAB,
  version: "1.0.0",
  options: {
    show_serial_number: true,
  },
};

const EmptyTabByType: Record<SKUPageContentTabType, SKUPageContentTab> = {
  [SKUPageContentTabType.MUSIC_TAB]: EMPTY_AUDIO_TAB,
  [SKUPageContentTabType.BUILDER_IO_TAB]: EMPTY_BUILDER_IO_TAB,
  [SKUPageContentTabType.PRODUCT_TAB]: EMPTY_PRODUCT_TAB,
  [SKUPageContentTabType.GALLERY_TAB]: EMPTY_GALLERY_TAB,
};

export const addTab = (
  pageContent: SKUPageContent,
  type: SKUPageContentTabType,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const newTab = get(EmptyTabByType, type);

  if (!newTab) {
    return pageContent;
  }

  const data = isArray(sanitized.data) ? sanitized.data : [];

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: concat(data, newTab),
  };
};

type UpdateAudioTabOptionsArgs = {
  title?: string;
  description?: string;
  show_header_title?: boolean;
  show_header_description?: boolean;
  show_audio_artist_name?: boolean;
  cover_image_url?: string;
};

export const updateAudioTabOptions = (
  pageContent: SKUPageContent,
  options: UpdateAudioTabOptionsArgs,
  tabIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isMusicTab = tab.type === SKUPageContentTabType.MUSIC_TAB;
    const isCurrent = index === tabIndex;

    if (!isMusicTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      version: "1.0.0",
      options: {
        ...tab.options,
        ...options,
      },
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateProductTabOptions = (
  pageContent: SKUPageContent,
  options: ProductTabOptions,
  tabIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isProductTab = tab.type === SKUPageContentTabType.PRODUCT_TAB;
    const isCurrent = index === tabIndex;

    if (!isProductTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      version: "1.0.0",
      options: {
        ...tab.options,
        ...options,
      },
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateTabDetails = (
  pageContent: SKUPageContent,
  options: SKUPageContentGenericTabOptions,
  tabIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isCurrent = index === tabIndex;

    if (!isCurrent) {
      return tab;
    }

    return {
      ...tab,
      version: "1.0.0",
      options: {
        ...tab.options,
        ...options,
      },
    } as SKUPageContentTab;
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const moveCMSTab = (
  pageContent: SKUPageContent,
  fromIndex: number,
  toIndex: number,
): SKUPageContent => {
  const newPageContentData = [...(pageContent.data ?? [])];

  const [removedTab] = newPageContentData.splice(fromIndex, 1);

  newPageContentData.splice(toIndex, 0, removedTab);

  return {
    ...pageContent,
    type: SKUPageContentType,
    data: newPageContentData,
  };
};

export type PlaylistTrackItem = {
  url: string;
  file_name: string;
};

export const addPlaylistTracks = (
  pageContent: SKUPageContent,
  tabIndex: number,
  tracks: PlaylistTrackItem[],
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isMusicTab = tab.type === SKUPageContentTabType.MUSIC_TAB;
    const isCurrent = index === tabIndex;

    if (!isMusicTab || !isCurrent) {
      return tab;
    }

    const createTrack = (track: PlaylistTrackItem): MusicTabData => ({
      type: "track",
      version: "1.0.0",
      options: {
        track_url: track.url,
        title: track.file_name,
        thumbnail_url: "",
        artist: "",
      },
    });

    return {
      ...tab,
      version: "1.0.0",
      data: [...tab.data, ...map(tracks, createTrack)],
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updatePlaylistTrack = (
  pageContent: SKUPageContent,
  tabIndex: number,
  trackIndex: number,
  options: Partial<MusicTabDataOptions>,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isMusicTab = tab.type === SKUPageContentTabType.MUSIC_TAB;
    const isCurrent = index === tabIndex;

    if (!isMusicTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      data: map(tab.data, (track, i) => {
        if (i !== trackIndex) {
          return track;
        }

        return {
          ...track,
          options: {
            ...track.options,
            ...options,
          },
        };
      }),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const removePlaylistTrack = (
  pageContent: SKUPageContent,
  tabIndex: number,
  trackIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isMusicTab = tab.type === SKUPageContentTabType.MUSIC_TAB;
    const isCurrent = index === tabIndex;

    if (!isMusicTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      data: filter(tab.data, (_, i) => i !== trackIndex),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateGalleryTabOptions = (
  pageContent: SKUPageContent,
  options: Partial<GalleryTabOptions>,
  tabIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isGalleryTab = tab.type === SKUPageContentTabType.GALLERY_TAB;
    const isCurrent = index === tabIndex;

    if (!isGalleryTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      options: {
        ...tab.options,
        ...options,
      },
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export type GalleryItem = {
  url: string;
  file_name: string;
};

export const addGalleryVideoItems = (
  pageContent: SKUPageContent,
  tabIndex: number,
  items: GalleryItem[],
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isGalleryTab = tab.type === SKUPageContentTabType.GALLERY_TAB;
    const isCurrent = index === tabIndex;

    if (!isGalleryTab || !isCurrent) {
      return tab;
    }

    const createVideo = (item: GalleryItem): GalleryTabDataVideo => ({
      type: "video",
      version: "1.0.0",
      options: {
        title: item.file_name,
        description: "",
        video_url: item.url,
      },
    });

    return {
      ...tab,
      data: [...tab.data, ...map(items, createVideo)],
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const addGalleryImageItems = (
  pageContent: SKUPageContent,
  tabIndex: number,
  items: GalleryItem[],
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isGalleryTab = tab.type === SKUPageContentTabType.GALLERY_TAB;
    const isCurrent = index === tabIndex;

    if (!isGalleryTab || !isCurrent) {
      return tab;
    }

    const createImage = (item: GalleryItem): GalleryTabDataImage => ({
      type: "image",
      version: "1.0.0",
      options: {
        title: item.file_name,
        description: "",
        image_url: item.url,
      },
    });

    return {
      ...tab,
      data: [...tab.data, ...map(items, createImage)],
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const addGalleryItems = (
  pageContent: SKUPageContent,
  tabIndex: number,
  items: GalleryItem[],
  type: "image" | "video",
) => {
  switch (type) {
    case "image":
      return addGalleryImageItems(pageContent, tabIndex, items);
    case "video":
      return addGalleryVideoItems(pageContent, tabIndex, items);
    default:
      return pageContent;
  }
};

export const updateGalleryItem = (
  pageContent: SKUPageContent,
  tabIndex: number,
  itemIndex: number,
  options: Partial<GalleryTabDataImage["options"]>,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isGalleryTab = tab.type === SKUPageContentTabType.GALLERY_TAB;
    const isCurrent = index === tabIndex;

    if (!isGalleryTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      data: map(tab.data, (item, i) => {
        if (i !== itemIndex || item.type !== "image") {
          return item;
        }

        return {
          ...item,
          options: {
            ...item.options,
            ...options,
          },
        };
      }),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateGalleryItemVideo = (
  pageContent: SKUPageContent,
  tabIndex: number,
  itemIndex: number,
  options: Partial<GalleryTabDataVideo["options"]>,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isGalleryTab = tab.type === SKUPageContentTabType.GALLERY_TAB;
    const isCurrent = index === tabIndex;

    if (!isGalleryTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      data: map(tab.data, (item, i) => {
        if (i !== itemIndex || item.type !== "video") {
          return item;
        }

        return {
          ...item,
          options: {
            ...item.options,
            ...options,
          },
        };
      }),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const removeGalleryItem = (
  pageContent: SKUPageContent,
  tabIndex: number,
  itemIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isGalleryTab = tab.type === SKUPageContentTabType.GALLERY_TAB;
    const isCurrent = index === tabIndex;

    if (!isGalleryTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      data: filter(tab.data, (_, i) => i !== itemIndex),
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

export const updateBuilderIOTabOptions = (
  pageContent: SKUPageContent,
  options: Partial<BuilderIOTabOptions>,
  tabIndex: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  const mapper = (tab: SKUPageContentTab, index: number): SKUPageContentTab => {
    const isBuilderTab = tab.type === SKUPageContentTabType.BUILDER_IO_TAB;
    const isCurrent = index === tabIndex;

    if (!isBuilderTab || !isCurrent) {
      return tab;
    }

    return {
      ...tab,
      options: {
        ...tab.options,
        ...options,
      },
    };
  };

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: map(sanitized.data, mapper),
  };
};

// When validation on the backend silently fails, we may end up with empty
// tab objects. This ensures that they are cleaned up properly on subsequent
// saves.
// We may consider doing this automatically after saves, but ultimately, the
// server should reject empty tabs.
// We also prepend the product tab if it doesn't exist.

export const sanitizePageContent = (
  pageContent: SKUPageContent,
): SKUPageContent => {
  const hasProductTab = some(
    pageContent.data,
    (tab) => tab.type === SKUPageContentTabType.PRODUCT_TAB,
  );

  const validTabs = filter(
    pageContent.data,
    (tab) => Object.keys(tab).length > 0,
  );

  return {
    ...pageContent,
    type: SKUPageContentType,
    data: hasProductTab ? validTabs : [EMPTY_PRODUCT_TAB, ...validTabs],
  };
};

export const deleteTab = (
  pageContent: SKUPageContent,
  index: number,
): SKUPageContent => {
  const sanitized = sanitizePageContent(pageContent);

  return {
    ...sanitized,
    type: SKUPageContentType,
    data: filter(sanitized.data, (_, i) => i !== index),
  };
};
